import { appendDeploymentRoutingQuery } from "@/utils/deploymentRouting";

// Helper to get the correct WebSocket URL with the right protocol
export const getWebSocketBaseUrl = () => {
  const envUrl = import.meta.env.VITE_WEBSOCKET_URL as string | undefined;
  if (envUrl) {
    // If we're on HTTPS, ensure we use WSS
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      envUrl.startsWith("ws://")
    ) {
      return envUrl.replace("ws://", "wss://");
    }
    return envUrl;
  }

  // Same-origin default for local Vite dev (CSP connect-src allows 'self' ws:// on the dev port).
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws`;
  }

  return undefined;
};

/**
 * Full WebSocket channel URL; recomputed on access so free-tier query param applies
 * after deployment routing tier is known.
 */
export const getWebSocketChannelUrl = (channel: string): string => {
  const baseUrl = getWebSocketBaseUrl();
  const path = `${baseUrl}/v2/collection/${channel}`;
  return appendDeploymentRoutingQuery(path);
};

// Common channel constants — getters so URLs reflect current deployment routing tier
export const WS_CHANNELS = {
  get CHAT() {
    return getWebSocketChannelUrl("pods.chat_messages");
  },
  get WORKER_CHAT() {
    return getWebSocketChannelUrl("pods.chat_messages");
  },
  get ACTIVITY() {
    return getWebSocketChannelUrl("pods.activities");
  },
  get DOCUMENTS() {
    return getWebSocketChannelUrl("pods.documents");
  },
  get TASKS() {
    return getWebSocketChannelUrl("pods.workflow_tasks");
  },
  get PERSONAL_TASKS() {
    return getWebSocketChannelUrl("ani_workers.personal_tasks");
  },
  get DATA() {
    return getWebSocketChannelUrl("pods.datasets");
  },
  get SYSTEM_TASKS() {
    return getWebSocketChannelUrl("pods.system_jobs");
  },
  get PORTFOLIOS() {
    return getWebSocketChannelUrl("pods.portfolio_definitions");
  },
  get PORTFOLIOS_WHATIF() {
    return getWebSocketChannelUrl("portfolios");
  },
  get POD_INSTANCES() {
    return getWebSocketChannelUrl("pods.pod_instances");
  },
  get CALL_TO_ACTIONS() {
    return getWebSocketChannelUrl("pods.call_to_actions");
  },
  get INBOX() {
    return getWebSocketChannelUrl("pods.inbox_items");
  },
  get ETL_RUN_STATES() {
    return getWebSocketChannelUrl("etl.run_states");
  },
  get ETL_STEPS_METADATA() {
    return getWebSocketChannelUrl("etl.steps_metadata");
  },
  get CASE_CHECKING_CASES() {
    return getWebSocketChannelUrl("case_checking.cases");
  },
  get CASE_CHECKING_DOCUMENTS() {
    return getWebSocketChannelUrl("case_checking.documents");
  },
};
